import { BookingStatus } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { formatDate, getDatesFromPeriod } from '@avantstay/backoffice-core'

import {
  BookingAcquisitionView,
  BookingRepeatView,
} from '@new/__global/components/Filters/Filters.types'
import {
  BookingDateFilterCondition,
  BookingDatesRawData,
  BookingsListResults,
} from '@new/domains/bookings/screens/bookingsList/BookingsList.types'
import { dollarToCents } from '@new/domains/payments/utils/dollarToCents'

export const parsePeriodFilters = (
  rawDateInfo: BookingDatesRawData,
): BookingDateFilterCondition | undefined => {
  const periodRange = rawDateInfo.period ? getDatesFromPeriod(rawDateInfo.period) : undefined

  const periodWithTemporalCondition =
    rawDateInfo.periodReference && rawDateInfo.period
      ? {
          date: formatDate(periodRange?.fromDate),
          condition: rawDateInfo.periodReference,
        }
      : undefined

  const periodWithRange =
    !rawDateInfo.periodReference && periodRange
      ? {
          from: formatDate(periodRange.fromDate),
          to: formatDate(periodRange.toDate),
        }
      : undefined

  return periodWithTemporalCondition || periodWithRange
    ? {
        date: periodWithTemporalCondition,
        range: periodWithRange,
      }
    : undefined
}

export const getBookingInfoDisabled = (status: BookingStatus) => {
  return status === BookingStatus.cancelled
}

export const formatRawPeriodValue = (value?: BookingDatesRawData | null) =>
  value?.period
    ? `${value.period}${value.periodReference ? `_reference=` : ''}${value.periodReference || ''}`
    : ''

export const parseRawPeriodValue = (value: string) => {
  const splittedValue = value?.split('_reference=')
  return {
    period: splittedValue[0],
    periodReference: splittedValue[1] || undefined,
  } as BookingDatesRawData
}

export const parsePriceFilter = ({
  minPrice,
  maxPrice,
  invertPriceRange,
}: {
  minPrice?: string
  maxPrice?: string
  invertPriceRange?: boolean
}) => {
  const price =
    minPrice !== undefined || maxPrice !== undefined
      ? {
          from:
            minPrice !== undefined ? dollarToCents(parseInt(minPrice, 10) as Dollars) : undefined,
          to: maxPrice !== undefined ? dollarToCents(parseInt(maxPrice, 10) as Dollars) : undefined,
          ...(invertPriceRange !== undefined ? { invertRange: invertPriceRange } : {}),
        }
      : undefined

  return price ? { filter: price } : undefined
}

export const parseLeadTimeFilter = (leadTime?: string) => {
  return leadTime ? parseInt(leadTime, 10) : undefined
}

export const parseAcquisitionFilter = (value?: string) => {
  const options = {
    [BookingAcquisitionView.allBookings]: undefined,
    [BookingAcquisitionView.onlyAcquisition]: true,
    [BookingAcquisitionView.nonAcquisition]: false,
  }

  return value ? options[value as keyof typeof options] : undefined
}

export const parseRepeatFilter = (value?: string) => {
  const options = {
    [BookingRepeatView.allGuests]: undefined,
    [BookingRepeatView.repeatGuestsOnly]: true,
    [BookingRepeatView.nonRepeatGuestsOnly]: false,
  }

  return value ? options[value as keyof typeof options] : undefined
}

export const parseBooleanFilterValue = (value?: string) => {
  if (value === 'yes') return true
  if (value === 'no') return false
  return undefined
}

export const parseTapeChartData = (rowData: BookingsListResults) => {
  return {
    'booking-id': rowData.id,
    markets: rowData.home.region?.id ?? '',
    t: rowData.home.name,
  }
}
